import { useState, useEffect, useCallback, useRef } from 'react';
import { BUILD_HASH } from '../version';
import { logger } from '../utils/logger';

// How often to poll /version.json while the tab is open.
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Detects when a newer build of the app has been deployed.
 *
 * The static /version.json file is regenerated on every Docker build and served
 * uncached, while the JS bundle (and its baked-in BUILD_HASH) is whatever the
 * current tab loaded. When the two hashes disagree, a new version is live.
 *
 * Returns:
 *   updateAvailable - boolean, true once a newer build is detected
 *   latestVersion   - { version, buildHash, buildTime } from the server (or null)
 *   dismiss()       - hide the prompt until the next detected change / reload
 *   reload()        - hard-reload to pick up the new build
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const dismissedHashRef = useRef(null);

  const checkVersion = useCallback(async () => {
    // Skip during local dev where there's no real build to compare against.
    if (BUILD_HASH === 'dev') return;

    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;

      const data = await res.json();
      if (!data || !data.buildHash) return;

      setLatestVersion(data);

      const isNewer =
        data.buildHash !== BUILD_HASH &&
        data.buildHash !== dismissedHashRef.current;

      if (isNewer) {
        setUpdateAvailable(true);
      }
    } catch (error) {
      // Network hiccups are non-fatal; we'll try again on the next tick.
      logger.debug('Version check failed', error);
    }
  }, []);

  const dismiss = useCallback(() => {
    if (latestVersion?.buildHash) {
      dismissedHashRef.current = latestVersion.buildHash;
    }
    setUpdateAvailable(false);
  }, [latestVersion]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (BUILD_HASH === 'dev') return undefined;

    // Check on mount, on an interval, and whenever the tab regains focus so a
    // long-lived tab notices deploys promptly without hammering the server.
    checkVersion();

    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', checkVersion);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', checkVersion);
    };
  }, [checkVersion]);

  return { updateAvailable, latestVersion, dismiss, reload };
}

export default useVersionCheck;
